import { Button, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { categoriesQuery, categoryActions } from "../../catalog";
import { messageFor } from "../../error-copy";
import styles from "./products.module.css";

/** A product's category: none, or one of the company's (made here if it's new). */
export function CategorySelect({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const queryClient = useQueryClient();
  const categories = useQuery(categoriesQuery);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => categoryActions.create(name.trim()),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ["catalog", "categories"] });
      onChange(c.id);
      setAdding(false);
      setName("");
    },
  });
  if (adding)
    return (
      <div className={styles.form}>
        <Input
          label="New category"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Bottled water"
        />
        {add.error ? <p className={styles.muted}>{messageFor(errorOf(add.error))}</p> : null}
        <span className={styles.actions}>
          <Button size="sm" disabled={!name.trim()} loading={add.isPending} onClick={() => add.mutate()}>
            Add the category
          </Button>
          <TextButton onClick={() => setAdding(false)}>Cancel</TextButton>
        </span>
      </div>
    );
  return (
    <div className={styles.form}>
      <Select
        label="Category (optional)"
        description="Groups products, so one price can cover all of them"
        value={value ?? "none"}
        onValueChange={(v) => onChange(v === "none" ? null : v)}
        options={[
          { value: "none", label: "No category" },
          ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
        ]}
      />
      <span>
        <TextButton onClick={() => setAdding(true)}>New category</TextButton>
      </span>
    </div>
  );
}

/** Every category, each renamed in place. A category in use by prices keeps them when renamed. */
export function CategoriesDialog({ onClose }: { onClose: () => void }): ReactNode {
  const categories = useQuery(categoriesQuery);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Categories"
        description="Products are grouped by category, and a price rule can cover a whole category."
      >
        <div className={styles.form}>
          {(categories.data ?? []).length === 0 ? (
            <p className={styles.muted}>No categories yet. Add one from a product's form.</p>
          ) : (
            (categories.data ?? []).map((c) => <CategoryRow key={c.id} id={c.id} name={c.name} />)
          )}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryRow({ id, name: current }: { id: string; name: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(current);
  const rename = useMutation({
    mutationFn: () => categoryActions.rename(id, name.trim()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalog"] }),
  });
  return (
    <div className={styles.pair}>
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <span className={styles.actions}>
        <Button
          variant="secondary"
          size="sm"
          disabled={!name.trim() || name.trim() === current}
          loading={rename.isPending}
          onClick={() => rename.mutate()}
        >
          Rename
        </Button>
        {rename.error ? (
          <Alert tone="danger" title="Not renamed">
            {messageFor(errorOf(rename.error))}
          </Alert>
        ) : null}
      </span>
    </div>
  );
}
